import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { format } from "date-fns";
import { sv as svLocale } from "date-fns/locale";
import type { NewsArticle } from "@/lib/posts";
import { trackEvent } from "@/lib/analytics";
import { useLanguage } from "@/i18n/useLanguage";

interface NewsCardProps {
  article: NewsArticle;
  showExcerpt?: boolean;
  className?: string;
}

const NewsCard = ({ article, showExcerpt = true, className }: NewsCardProps) => {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const categoryLabel = t(`newsPage.categories.${article.category}`, {
    defaultValue: article.category,
  });
  const dateLabel =
    lang === "sv"
      ? format(article.date, "d MMMM yyyy", { locale: svLocale })
      : format(article.date, "MMMM d, yyyy");

  const handleClick = () =>
    trackEvent("news_article_click", {
      article_id: article.id,
      article_title: article.title,
      article_category: article.category,
      is_external: article.isExternal,
    });

  // All cards navigate to the on-domain detail page; external posts surface
  // the outbound LinkedIn CTA from inside that page.
  const linkProps = { to: article.href, onClick: handleClick };

  return (
    <Card
      className={cn(
        "group overflow-hidden hover:-translate-y-1 hover:shadow-glow-card hover:border-primary/40 transition-all duration-300 flex flex-col h-full bg-card/80 backdrop-blur-sm",
        className,
      )}
    >
      <Link {...linkProps} aria-label={article.title} className="block overflow-hidden relative">
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            width="400"
            height="192"
          />
        ) : (
          <div className="h-48 w-full bg-gradient-brand opacity-70 transition-transform duration-500 group-hover:scale-105" />
        )}
      </Link>

      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-xs">
            {categoryLabel}
          </Badge>
          <span className="text-xs text-muted-foreground">{dateLabel}</span>
        </div>
        <CardTitle className="text-xl md:text-2xl font-bold">
          <Link
            {...linkProps}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm transition-colors hover:text-primary group-hover:text-primary hover:underline underline-offset-4 decoration-primary/60"
          >
            {article.title}
          </Link>
        </CardTitle>
      </CardHeader>

      {showExcerpt && (article.excerpt?.trim() || article.author) && (
        <CardContent className="flex flex-col flex-1">
          {article.excerpt?.trim() && (
            <CardDescription className="text-sm md:text-base mb-4 line-clamp-3">
              {article.excerpt}
            </CardDescription>
          )}

          {article.author && (
            <p className="text-xs text-muted-foreground mt-auto">
              {t("newsPage.card.by", { author: article.author })}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default NewsCard;
